import { userManagement } from "@/services/userManagement";
import { delay, makePassword } from "@/services/utils";
import {
	getRmsConsultaPayload,
	getRmsDisplayName,
	getRmsReprocessPayload,
	parseRmsStatus,
} from '@/utils/rms';
import { equalsIgnoreCase } from "@/utils/strings";

const PERSISTENCE_DELAY_MS = 1000;

const initialDialog = { show: false, icon: '', title: '', text: '', actionType: null };
const initialToast = { show: false, type: '', message: '' };
const initialUserDetailsModal = { show: false, mode: 'view', user: null };
const initialAddDependentModal = { show: false, titular: null };

export default {
	namespaced: true,

	state: {
		users: [],
		totalItems: 0,

		statusFilter: 'Ativo',
		productFilter: null,
		hasDependentFilter: null,
		options: { page: 1, itemsPerPage: 10 },
		isSearchActive: false,
		refreshKey: 0,

		loading: false,
		rmsLoading: null,

		selectedUser: null,
		selectedDependent: null,

		expandedItems: [],

		dialog: { ...initialDialog },
		rmsDialog: { ...initialDialog },
		userDetailsModal: { ...initialUserDetailsModal },
		addDependentModal: { ...initialAddDependentModal },

		toast: { ...initialToast },
	},

	getters: {
		users: (state) => state.users,
		totalItems: (state) => state.totalItems,
		statusFilter: (state) => state.statusFilter,
		productFilter: (state) => state.productFilter,
		hasDependentFilter: (state) => state.hasDependentFilter,
		isActive: (state) => equalsIgnoreCase(state.statusFilter, 'ativo'),
		options: (state) => state.options,
		isSearchActive: (state) => state.isSearchActive,
		refreshKey: (state) => state.refreshKey,
		loading: (state) => state.loading,
		rmsLoading: (state) => state.rmsLoading,
		selectedUser: (state) => state.selectedUser,
		selectedDependent: (state) => state.selectedDependent,
		expandedItems: (state) => state.expandedItems,
		dialog: (state) => state.dialog,
		rmsDialog: (state) => state.rmsDialog,
		userDetailsModal: (state) => state.userDetailsModal,
		addDependentModal: (state) => state.addDependentModal,
		toast: (state) => state.toast,
	},

	mutations: {
		SET_USERS(state, { items, total }) {
			state.users = items;
			state.totalItems = total;
		},
		SET_STATUS_FILTER(state, value) { state.statusFilter = value; },
		SET_PRODUCT_FILTER(state, value) { state.productFilter = value; },
		SET_HAS_DEPENDENT_FILTER(state, value) { state.hasDependentFilter = value; },
		SET_OPTIONS(state, value) { state.options = value; },
		SET_IS_SEARCH_ACTIVE(state, value) { state.isSearchActive = value; },
		INCREMENT_REFRESH_KEY(state) { state.refreshKey++; },
		SET_LOADING(state, value) { state.loading = value; },
		SET_RMS_LOADING(state, id) { state.rmsLoading = id; },
		SET_SELECTED_USER(state, user) { state.selectedUser = user; },
		SET_SELECTED_DEPENDENT(state, dependent) { state.selectedDependent = dependent; },
		SET_EXPANDED_ITEMS(state, items) { state.expandedItems = items; },
		SET_DIALOG(state, dialog) { state.dialog = { ...initialDialog, ...dialog }; },
		CLOSE_DIALOG(state) { state.dialog.show = false; },
		SET_RMS_DIALOG(state, dialog) { state.rmsDialog = { ...initialDialog, ...dialog }; },
		CLOSE_RMS_DIALOG(state) { state.rmsDialog.show = false; },
		SET_USER_DETAILS_MODAL(state, modal) { state.userDetailsModal = { ...initialUserDetailsModal, ...modal }; },
		CLOSE_USER_DETAILS_MODAL(state) { state.userDetailsModal.show = false; },
		SET_ADD_DEPENDENT_MODAL(state, modal) { state.addDependentModal = { ...initialAddDependentModal, ...modal }; },
		CLOSE_ADD_DEPENDENT_MODAL(state) { state.addDependentModal.show = false; },
		SET_TOAST(state, toast) { state.toast = { ...initialToast, ...toast }; },
		HIDE_TOAST(state) { state.toast.show = false; },
		UPDATE_USER_RMS_STATUS(state, { userId, rmsStatus }) {
			state.users = state.users.map((user) => {
				if (user.id === userId) return { ...user, rmsStatus };
				if (user.dependents?.some((dependent) => dependent.id === userId)) {
					return {
						...user,
						dependents: user.dependents.map((dependent) =>
							dependent.id === userId ? { ...dependent, rmsStatus } : dependent
						),
					};
				}
				return user;
			});
		},
	},

	actions: {
		showToast({ commit }, toast) {
			commit('SET_TOAST', { show: true, ...toast });
			setTimeout(() => commit('HIDE_TOAST'), 3000);
		},

		async fetchUsers({ commit, state, dispatch }) {
			commit('SET_LOADING', true);
			commit('SET_IS_SEARCH_ACTIVE', false);
			try {
				const { page, itemsPerPage } = state.options;
				const offset = (page - 1) * itemsPerPage;
				const { data } = await userManagement.getOrdersUsers({
					status: state.statusFilter.toLowerCase(),
					product: state.productFilter,
					hasDependent: state.hasDependentFilter,
					offset,
					limit: itemsPerPage,
				});
				commit('SET_USERS', { items: data?.items || [], total: data?.total || 0 });
			} catch (error) {
				commit('SET_USERS', { items: [], total: 0 });
				dispatch('showToast', { type: 'error', message: 'Não foi possível buscar os usuários. Tente novamente!' });
				throw error;
			} finally {
				commit('SET_LOADING', false);
			}
		},

		setStatusFilter({ commit, state, dispatch }, status) {
			commit('SET_STATUS_FILTER', status);
			commit('SET_OPTIONS', { ...state.options, page: 1 });
			return dispatch('fetchUsers');
		},

		setProductFilter({ commit, state, dispatch }, value) {
			commit('SET_PRODUCT_FILTER', value);
			commit('SET_OPTIONS', { ...state.options, page: 1 });
			return dispatch('fetchUsers');
		},

		setHasDependentFilter({ commit, state, dispatch }, value) {
			commit('SET_HAS_DEPENDENT_FILTER', value);
			commit('SET_OPTIONS', { ...state.options, page: 1 });
			return dispatch('fetchUsers');
		},

		updateOptions({ commit, dispatch, state }, options) {
			if (state.isSearchActive) {
				commit('SET_IS_SEARCH_ACTIVE', false);
				return;
			}
			commit('SET_OPTIONS', options);
			return dispatch('fetchUsers');
		},

		resetFilters({ commit, state, dispatch }) {
			commit('SET_STATUS_FILTER', 'Ativo');
			commit('SET_PRODUCT_FILTER', null);
			commit('SET_HAS_DEPENDENT_FILTER', null);
			commit('SET_OPTIONS', { ...state.options, page: 1 });
			commit('SET_IS_SEARCH_ACTIVE', false);
			commit('INCREMENT_REFRESH_KEY');
			return dispatch('fetchUsers');
		},

		setUsersFromSearch({ commit, state }, { users, totalItems }) {
			commit('SET_IS_SEARCH_ACTIVE', true);
			commit('SET_USERS', { items: users, total: totalItems });
			commit('SET_OPTIONS', { ...state.options, page: 1 });
		},

		async searchUserByDocument({ commit }, { document, status }) {
			commit('SET_LOADING', true);
			try {
				const { data } = await userManagement.getUserByDocument({ document, status });
				return data?.items || [];
			} finally {
				commit('SET_LOADING', false);
			}
		},

		setExpandedItems({ commit }, items) {
			commit('SET_EXPANDED_ITEMS', items);
		},

		openConfirmDialog({ commit }, { icon, title, text, actionType, user, dependent }) {
			if (user) commit('SET_SELECTED_USER', user);
			if (dependent) commit('SET_SELECTED_DEPENDENT', dependent);
			commit('SET_DIALOG', { show: true, icon, title, text, actionType });
		},

		closeDialog({ commit }) {
			commit('CLOSE_DIALOG');
		},

		async confirmDialog({ state, dispatch, commit }) {
			const { actionType } = state.dialog;
			if (actionType === 'reactivateUser') await dispatch('reactivateUser', state.selectedUser);
			if (actionType === 'deactivateUser') await dispatch('deactivateUser', state.selectedUser);
			if (actionType === 'removeDependent') {
				await dispatch('removeDependent', {
					orderId: state.selectedUser?.id,
					dependent: state.selectedDependent,
				});
			}
			commit('CLOSE_DIALOG');
		},

		openRmsDialog({ commit }, item) {
			commit('SET_SELECTED_USER', item);
			commit('SET_RMS_DIALOG', {
				show: true,
				icon: 'mdi-account-sync-outline',
				title: 'Reprocessar RMS',
				text: `O reprocessamento de ${getRmsDisplayName(item)} pode demorar até 15 minutos para ser concluído. Deseja continuar?`,
			});
		},

		closeRmsDialog({ commit }) {
			commit('CLOSE_RMS_DIALOG');
		},

		async confirmRmsDialog({ state, dispatch, commit }) {
			await dispatch('reprocessRms', state.selectedUser);
			commit('CLOSE_RMS_DIALOG');
		},

		openUserDetailsModal({ commit }, { user, mode = 'view' }) {
			commit('SET_USER_DETAILS_MODAL', { show: true, mode, user });
		},

		closeUserDetailsModal({ commit }) {
			commit('CLOSE_USER_DETAILS_MODAL');
		},

		openAddDependentModal({ commit }, { titular }) {
			commit('SET_ADD_DEPENDENT_MODAL', { show: true, titular });
		},

		closeAddDependentModal({ commit }) {
			commit('CLOSE_ADD_DEPENDENT_MODAL');
		},

		async updateUserDetails({ commit, dispatch }, { id, data }) {
			commit('SET_LOADING', true);
			try {
				await userManagement.updateUser(id, data);
				await delay(PERSISTENCE_DELAY_MS);
				await dispatch('fetchUsers');
				commit('CLOSE_USER_DETAILS_MODAL');
				dispatch('showToast', { type: 'success', message: 'Usuário atualizado com sucesso!' });
			} catch (error) {
				dispatch('showToast', { type: 'error', message: 'Não foi possível atualizar o usuário. Tente novamente!' });
				throw error;
			} finally {
				commit('SET_LOADING', false);
			}
		},

		async reactivateUser({ dispatch }, user) {
			try {
				await userManagement.reactivateUser(user.id);
				await delay(PERSISTENCE_DELAY_MS);
				await dispatch('fetchUsers');
				dispatch('showToast', { type: 'success', message: `Usuário: ${user?.user?.name || user?.name}, reativado com sucesso!` });
			} catch (error) {
				dispatch('showToast', { type: 'error', message: 'Não foi possível reativar o usuário. Tente novamente!' });
				throw error;
			}
		},

		async deactivateUser({ dispatch }, user) {
			try {
				await userManagement.deactivateUser({ id: user.id, cancel_reason: 'Cancelado VC Pode' });
				await delay(PERSISTENCE_DELAY_MS);
				await dispatch('fetchUsers');
				dispatch('showToast', { type: 'success', message: 'Usuário inativado com sucesso!' });
			} catch (error) {
				dispatch('showToast', { type: 'error', message: 'Não foi possível inativar o usuário. Tente novamente!' });
				throw error;
			}
		},

		async removeDependent({ dispatch }, { orderId, dependent }) {
			try {
				await userManagement.removeDependent({ orderId, dependentId: dependent.id });
				await delay(PERSISTENCE_DELAY_MS);
				await dispatch('fetchUsers');
				dispatch('showToast', { type: 'success', message: `Dependente: ${dependent?.name}, removido com sucesso!` });
			} catch (error) {
				dispatch('showToast', { type: 'error', message: 'Não foi possível remover o dependente. Tente novamente!' });
				throw error;
			}
		},

		async addDependent({ commit, dispatch }, { titular, dependentData }) {
			commit('SET_LOADING', true);
			let dependentCreated = false;
			try {
				const password = makePassword(dependentData?.document);
				const payload = { ...dependentData, password, profile: 'dependent', titular: titular?.document };

				const { data: newDependent } = await userManagement.internalAddDependent(payload);
				dependentCreated = true;

				await Promise.all([
					userManagement.sendNewPassword({ name: payload?.name, password, username: payload?.email }),
					userManagement.updateOrder({ id: titular?.id, dependents: [newDependent?.item?.id] }),
				]);

				await delay(PERSISTENCE_DELAY_MS);
				await dispatch('fetchUsers');
				commit('CLOSE_ADD_DEPENDENT_MODAL');
				dispatch('showToast', { type: 'success', message: 'Dependente adicionado com sucesso!' });
			} catch (error) {
				const message = dependentCreated
					? 'O dependente foi criado, mas houve falha ao concluir o vínculo com o titular e/ou o envio da senha. Verifique o cadastro e tente novamente se necessário.'
					: 'Não foi possível adicionar o dependente. Tente novamente!';
				dispatch('showToast', { type: 'error', message });
				throw error;
			} finally {
				commit('SET_LOADING', false);
			}
		},

		async refreshRmsStatus({ commit, dispatch }, item) {
			commit('SET_RMS_LOADING', item.id);
			try {
				const payload = getRmsConsultaPayload(item);
				if (!payload) {
					dispatch('showToast', { type: 'error', message: 'Dados insuficientes para consultar status na RMS.' });
					return;
				}
				const { data } = await userManagement.checkRmsStatus(payload);
				const rmsStatus = parseRmsStatus(data);
				commit('UPDATE_USER_RMS_STATUS', { userId: item.id, rmsStatus });
			} catch (error) {
				dispatch('showToast', { type: 'error', message: 'Não foi possível atualizar o usuário na RMS. Tente novamente!' });
				throw error;
			} finally {
				commit('SET_RMS_LOADING', null);
			}
		},

		async reprocessRms({ commit, dispatch }, item) {
			commit('SET_RMS_LOADING', item.id);
			try {
				const { data } = await userManagement.reprocessRms(getRmsReprocessPayload(item));
				if (!data) {
					dispatch('showToast', { type: 'error', message: 'Resposta inesperada da RMS. Tente novamente!' });
					return;
				}
				await delay(3000);
				await dispatch('refreshRmsStatus', item);
				dispatch('showToast', { type: 'success', message: `Usuário: ${getRmsDisplayName(item)}, reprocessado na RMS com sucesso!` });
			} catch (error) {
				dispatch('showToast', { type: 'error', message: 'Não foi possível reprocessar o usuário na RMS. Tente novamente!' });
				throw error;
			} finally {
				commit('SET_RMS_LOADING', null);
			}
		},
	},
};
